import { createContext, useContext, useMemo, type ReactNode } from "react";
import { useLocation } from "@tanstack/react-router";
import {
  RESOURCE_SLUG_EN_TO_ES,
  RESOURCE_SLUG_ES_TO_EN,
} from "./resource-slugs";

export type Locale = "en" | "es";

const LocaleContext = createContext<Locale>("en");

export function LocaleProvider({ children }: { children: ReactNode }) {
  const loc = useLocation();
  const locale: Locale = loc.pathname.startsWith("/es") ? "es" : "en";
  return <LocaleContext.Provider value={locale}>{children}</LocaleContext.Provider>;
}

export function useLocale(): Locale {
  return useContext(LocaleContext);
}

/**
 * Mirror EN routes to ES counterparts. Used by the language toggle.
 */
const EN_TO_ES: Record<string, string> = {
  "/": "/es",
  "/services": "/es/servicios",
  "/shopify-experts": "/es/desarrollo-shopify",
  "/shopify-migration": "/es/migracion-shopify",
  "/shopify-performance": "/es/rendimiento",
  "/shopify-cro": "/es/cro-shopify",
  "/shopify-technical-seo": "/es/seo-tecnico-shopify",
  "/analytics-ai": "/es/analitica-ai",
  "/retainer": "/es/retainer",
  "/work": "/es/proyectos",
  "/about": "/es/nosotros",
  "/trust": "/es/confianza",
  "/partners/referrals": "/es/partners/referidos",
  "/partners/referral-terms": "/es/partners/terminos-referidos",
  "/pricing": "/es/precios",
  "/contact": "/es/contacto",
  "/get-a-quote": "/es/cotizacion",
  "/blog": "/es/blog",
  "/pulse": "/es/pulse",
  "/privacy": "/es/privacidad",
  "/terms": "/es/terminos",
  "/resources": "/es/recursos",
  "/audit": "/es/auditoria",
  "/geo-readiness": "/es/auditoria-geo",
  "/shopify-marketing-strategy": "/es/estrategia-marketing-shopify",
  "/about/samuel-noriega": "/es/nosotros/samuel-noriega",
  "/resources/performance": "/es/recursos/rendimiento",
  "/resources/migration": "/es/recursos/migracion",
  "/resources/conversion": "/es/recursos/conversion",
  "/shopify-plus-agency": "/es/agencia-shopify-plus",
  "/lp/shopify-agency": "/es/lp/agencia-shopify",
  "/shopify-integration-services": "/es/integraciones-shopify",
  "/shopify-app-development": "/es/desarrollo-apps-shopify",
  "/resources/shopify-performance-optimization-guide": "/es/recursos/guia-optimizacion-rendimiento-shopify",
  "/resources/shopify-replatforming-playbook": "/es/recursos/playbook-replatforming-shopify",
  "/resources/technical-seo": "/es/recursos/seo-tecnico",
  "/resources/shopify-conversion-rate-optimization-playbook": "/es/recursos/playbook-cro-shopify",
};
const ES_TO_EN: Record<string, string> = Object.fromEntries(
  Object.entries(EN_TO_ES).map(([k, v]) => [v, k]),
);

/**
 * Path prefixes that have no localized counterpart. The header's language
 * toggle is hidden on these pages; hreflang fallbacks still return the
 * locale homepage so static SEO tags never break the build.
 */
const NO_COUNTERPART_PREFIXES = [
  "/checkout",
  "/order-confirmed",
  "/portal",
  "/quote",
  "/partner",
  "/partner-login",
  "/admin",
  "/auth",
];

export function hasAlternatePath(path: string): boolean {
  const base = path.split(/[?#]/)[0];
  if (NO_COUNTERPART_PREFIXES.some((p) => base === p || base.startsWith(`${p}/`))) {
    return false;
  }
  if (base.startsWith("/es/recursos/") || base.startsWith("/resources/")) return true;
  if (base.startsWith("/es")) return base in ES_TO_EN || base === "/es";
  return base in EN_TO_ES;
}

export function alternatePath(path: string, target: Locale): string {
  const base = path.split(/[?#]/)[0];
  if (target === "es") {
    if (base.startsWith("/es")) return base;
    if (base.startsWith("/resources/")) {
      const enSlug = base.slice("/resources/".length);
      const esSlug = RESOURCE_SLUG_EN_TO_ES[enSlug] ?? enSlug;
      return `/es/recursos/${esSlug}`;
    }
    return EN_TO_ES[base] ?? "/es";
  }
  if (base.startsWith("/es/recursos/")) {
    const esSlug = base.slice("/es/recursos/".length);
    const enSlug = RESOURCE_SLUG_ES_TO_EN[esSlug] ?? esSlug;
    return `/resources/${enSlug}`;
  }
  if (base.startsWith("/es")) return ES_TO_EN[base] ?? (base.replace(/^\/es/, "") || "/");
  return base;
}

export function useAlternatePath(target: Locale): string {
  const loc = useLocation();
  return useMemo(() => alternatePath(loc.pathname, target), [loc.pathname, target]);
}

export function useHasAlternatePath(): boolean {
  const loc = useLocation();
  return useMemo(() => hasAlternatePath(loc.pathname), [loc.pathname]);
}

export const SITE_URL = "https://shugert.com.mx";
